/**
 * Deteccao de fala por RMS, com histerese e cauda — o anel do avatar so pisca
 * quando a pessoa realmente fala, e nao a cada silaba.
 */

/** Recebe apenas as transicoes: nunca e chamado duas vezes com o mesmo valor. */
export type SpeakingChange = (id: string, speaking: boolean) => void;

/** Acima disso comeca a falar. */
const ENTER_LEVEL = 0.045;

/** Abaixo disso pode parar, depois da cauda. */
const EXIT_LEVEL = 0.02;

/** Tempo de silencio ate desligar o indicador. */
const TAIL_MS = 350;

const TICK_MS = 100;

type Entry = {
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  buffer: Float32Array<ArrayBuffer>;
  speaking: boolean;
  loudAt: number;
  /** O Chrome so entrega audio remoto ao WebAudio quando o stream tambem esta
   *  ligado a um elemento de midia; este fica mudo so para manter o fluxo. */
  keeper: HTMLAudioElement;
};

export class SpeakingDetector {
  private readonly onChange: SpeakingChange;
  private readonly entries = new Map<string, Entry>();
  private context: AudioContext | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private destroyed = false;

  constructor(onChange: SpeakingChange) {
    this.onChange = onChange;
  }

  attach(id: string, stream: MediaStream): void {
    if (this.destroyed) return;
    if (stream.getAudioTracks().length === 0) return;
    this.detach(id);

    const context = this.ensureContext();
    if (!context) return;

    let source: MediaStreamAudioSourceNode;
    try {
      source = context.createMediaStreamSource(stream);
    } catch {
      return;
    }

    const analyser = context.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.2;
    source.connect(analyser);

    const keeper = new Audio();
    keeper.srcObject = stream;
    keeper.muted = true;
    keeper.autoplay = true;
    void keeper.play().catch(() => undefined);

    this.entries.set(id, {
      source,
      analyser,
      buffer: new Float32Array(analyser.fftSize),
      speaking: false,
      loudAt: 0,
      keeper,
    });

    if (!this.timer) this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  detach(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) return;
    this.entries.delete(id);
    try {
      entry.source.disconnect();
    } catch {
      // no fechamento do contexto o no ja pode estar solto
    }
    entry.keeper.pause();
    entry.keeper.srcObject = null;
    if (entry.speaking && !this.destroyed) this.onChange(id, false);
    if (this.entries.size === 0 && this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const id of [...this.entries.keys()]) this.detach(id);
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    const context = this.context;
    this.context = null;
    if (context) void context.close().catch(() => undefined);
  }

  private ensureContext(): AudioContext | null {
    if (this.context) {
      if (this.context.state === "suspended") void this.context.resume().catch(() => undefined);
      return this.context;
    }
    try {
      this.context = new AudioContext({ latencyHint: "interactive" });
    } catch {
      return null;
    }
    if (this.context.state === "suspended") void this.context.resume().catch(() => undefined);
    return this.context;
  }

  private tick(): void {
    const now = Date.now();
    for (const [id, entry] of this.entries) {
      entry.analyser.getFloatTimeDomainData(entry.buffer);
      const level = rms(entry.buffer);
      if (level > ENTER_LEVEL) {
        entry.loudAt = now;
        if (!entry.speaking) {
          entry.speaking = true;
          this.onChange(id, true);
        }
        continue;
      }
      if (entry.speaking && level < EXIT_LEVEL && now - entry.loudAt > TAIL_MS) {
        entry.speaking = false;
        this.onChange(id, false);
      }
    }
  }
}

function rms(buffer: ArrayLike<number>): number {
  let sum = 0;
  for (let index = 0; index < buffer.length; index += 1) {
    const value = buffer[index];
    sum += value * value;
  }
  return Math.sqrt(sum / buffer.length);
}
